import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Info } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { 
  Loader2, 
  Upload, 
  X, 
  MapPin, 
  Building2, 
  Wifi,
  Palmtree as Trees,
  Car,
  Droplets as Pool,
  Dumbbell,
  Shield,
  Tv,
  Wind,
  Sun,
  Footprints,
  Bath,
  Sofa,
  UtensilsCrossed,
  Baby,
  Dog,
  Package as Warehouse,
  Shirt,
  Home as Fence,
  Snowflake,
  Waves,
  Lightbulb,
  Lock,
  Camera,
  Gamepad,
  ParkingCircle as Parking,
  MoveVertical as Elevator,
  Droplet,
  Zap,
  Flame,
  Globe,
  Radio,
  Trash2,
  Wrench,
  ShieldCheck,
  UserRound,
  Check,
  ChevronLeft,
  ChevronRight,
  Send,
  RefreshCw
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Property } from '@/types/property';
import { cn } from '@/lib/utils';
import { PropertyMap } from '@/components/PropertyMap';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DEFAULT_CENTER } from '@/config/constants';
import { useAuth } from '@/hooks/useAuth';
import axiosInstance from "@/config/axios";
import { CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";

// Fix for default Leaflet icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Interactive Map Component for Property Management
function InteractiveMap({ location, onLocationChange }: { 
  location: { lat: number, lng: number }, 
  onLocationChange: (lat: number, lng: number) => void 
}) {
  const map = useMap();

  useEffect(() => {
    map.setView([location.lat, location.lng]);
  }, [location.lat, location.lng, map]);

  useMapEvents({
    click(e) {
      onLocationChange(e.latlng.lat, e.latlng.lng);
    },
  });

  return (
    <Marker 
      position={[location.lat, location.lng]} 
      draggable={true}
      eventHandlers={{
        dragend: (e) => {
          const marker = e.target;
          const position = marker.getLatLng();
          onLocationChange(position.lat, position.lng);
        },
      }}
    />
  );
}

interface PropertyFormData {
  title: string;
  description: string;
  price: number;
  operationType: string;
  propertyType: string;
  address: string;
  city: string;
  location: {
    lat: number;
    lng: number;
  };
  beds: number;
  baths: number;
  sqft: number;
  estacionamientos: number;
  antiguedad: number;
  condition: string;
  orientacion: string;
  piso: string | undefined;
  amoblado: boolean;
  features: string[];
  images: string[];
  zonasComunes: string[];
  servicios: string[];
  descripcionZona: string;
  vendedor: {
    nombre: string;
    descripcion: string;
    telefono: string;
    email: string;
  };
}

interface NominatimSuggestion {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  address: {
    road?: string;
    house_number?: string;
    suburb?: string;
    city?: string;
    state?: string;
    country?: string;
    postcode?: string;
  };
}

const PROPERTY_TYPES = [
  "Casa",
  "Apartamento",
  "Casa de Playa",
  "Villa",
  "Townhouse",
  "Terreno",
  "Local Comercial",
  "Oficina",
  "Casa Comercial",
  "Industrial",
  "Parcelación"
];

const AMENITIES = [
  "Piscina",
  "Gimnasio",
  "Seguridad 24/7",
  "Estacionamiento",
  "Área de BBQ",
  "Jardín",
  "Terraza",
  "Aires Acondicionados",
  "Ascensor",
  "Área de Juegos",
  "Sala de Reuniones",
  "Lobby",
  "Cámaras de Seguridad"
];

const COMMON_AREAS = [
  "Piscina",
  "Gimnasio",
  "Parque Infantil",
  "Salón de Fiestas",
  "Área de BBQ",
  "Cancha Deportiva",
  "Área Social",
  "Jardines",
  "Lobby",
  "Sala de Reuniones"
];

const SERVICES = [
  "Agua",
  "Electricidad",
  "Gas",
  "Internet",
  "Cable TV",
  "Recolección de Basura",
  "Mantenimiento",
  "Vigilancia Privada",
  "Conserje"
];

const CONDITIONS = [
  "A Estrenar",
  "Excelente",
  "Muy Bueno",
  "Bueno",
  "A Remodelar"
];

const ORIENTATIONS = [
  "Norte",
  "Sur",
  "Este",
  "Oeste",
  "Noreste",
  "Noroeste",
  "Sureste",
  "Suroeste"
];

const VENEZUELA_CITIES = [
  "Caracas",
  "Maracaibo",
  "Valencia",
  "Barquisimeto",
  "Maracay",
  "Ciudad Guayana",
  "Barcelona",
  "Maturín",
  "Puerto La Cruz",
  "Porlamar",
  "Mérida",
  "San Cristóbal",
  "Cumaná",
  "Punto Fijo",
  "Barinas",
  "Acarigua",
  "Los Teques",
  "Puerto Cabello",
  "Coro",
  "Guanare"
];

// Definir las características con sus iconos correspondientes
const PROPERTY_FEATURES = {
  "Internet de alta velocidad": { icon: Wifi, color: "text-blue-500" },
  "Áreas verdes": { icon: Trees, color: "text-green-500" },
  "Estacionamiento techado": { icon: Car, color: "text-gray-600" },
  "Piscina": { icon: Pool, color: "text-blue-400" },
  "Gimnasio": { icon: Dumbbell, color: "text-purple-500" },
  "Seguridad 24/7": { icon: Shield, color: "text-primary" },
  "TV por cable": { icon: Tv, color: "text-red-500" },
  "Aire acondicionado": { icon: Wind, color: "text-cyan-500" },
  "Vista panorámica": { icon: Sun, color: "text-yellow-500" },
  "Área de juegos": { icon: Footprints, color: "text-pink-500" },
  "Baño de servicio": { icon: Bath, color: "text-indigo-500" },
  "Amoblado": { icon: Sofa, color: "text-amber-500" },
  "Cocina equipada": { icon: UtensilsCrossed, color: "text-orange-500" },
  "Área infantil": { icon: Baby, color: "text-rose-500" },
  "Pet friendly": { icon: Dog, color: "text-emerald-500" },
  "Depósito": { icon: Warehouse, color: "text-gray-500" },
  "Área de lavado": { icon: Shirt, color: "text-blue-300" },
  "Terraza": { icon: Fence, color: "text-brown-500" },
  "Calefacción": { icon: Snowflake, color: "text-blue-600" },
  "Vista al mar": { icon: Waves, color: "text-blue-500" },
  "Iluminación natural": { icon: Lightbulb, color: "text-yellow-400" },
  "Acceso controlado": { icon: Lock, color: "text-gray-700" },
  "Circuito cerrado": { icon: Camera, color: "text-gray-600" },
  "Sala de juegos": { icon: Gamepad, color: "text-violet-500" },
  "Estacionamiento visitantes": { icon: Parking, color: "text-blue-700" },
  "Ascensor": { icon: Elevator, color: "text-gray-500" }
};

// Definir los servicios con sus iconos correspondientes
const SERVICES_WITH_ICONS = {
  "Agua": { icon: Droplet, color: "text-blue-500" },
  "Electricidad": { icon: Zap, color: "text-yellow-500" },
  "Gas": { icon: Flame, color: "text-orange-500" },
  "Internet": { icon: Globe, color: "text-blue-600" },
  "Cable TV": { icon: Radio, color: "text-red-500" },
  "Recolección de Basura": { icon: Trash2, color: "text-gray-600" },
  "Mantenimiento": { icon: Wrench, color: "text-gray-700" },
  "Vigilancia Privada": { icon: ShieldCheck, color: "text-primary" },
  "Conserje": { icon: UserRound, color: "text-indigo-500" }
};

const COUNTRIES = [
  { code: "VE", name: "Venezuela" },
  { code: "CO", name: "Colombia" },
  { code: "PE", name: "Perú" },
  { code: "EC", name: "Ecuador" },
  { code: "CL", name: "Chile" },
  { code: "AR", name: "Argentina" },
  { code: "BR", name: "Brasil" },
  { code: "MX", name: "México" }
];

const REGIONS_BY_COUNTRY = {
  VE: [
    'Amazonas', 'Anzoátegui', 'Apure', 'Aragua', 'Barinas', 'Bolívar', 'Carabobo',
    'Cojedes', 'Delta Amacuro', 'Distrito Capital', 'Falcón', 'Guárico', 'Lara',
    'Mérida', 'Miranda', 'Monagas', 'Nueva Esparta', 'Portuguesa', 'Sucre', 'Táchira',
    'Trujillo', 'Vargas', 'Yaracuy', 'Zulia'
  ],
  CO: [
    'Amazonas', 'Antioquia', 'Arauca', 'Atlántico', 'Bolívar', 'Boyacá', 'Caldas',
    'Caquetá', 'Casanare', 'Cauca', 'Cesar', 'Chocó', 'Córdoba', 'Cundinamarca',
    'Guainía', 'Guaviare', 'Huila', 'La Guajira', 'Magdalena', 'Meta', 'Nariño',
    'Norte de Santander', 'Putumayo', 'Quindío', 'Risaralda', 'San Andrés y Providencia',
    'Santander', 'Sucre', 'Tolima', 'Valle del Cauca', 'Vaupés', 'Vichada'
  ],
  CL: [
    'Arica y Parinacota', 'Tarapacá', 'Antofagasta', 'Atacama', 'Coquimbo',
    'Valparaíso', 'Metropolitana de Santiago', 'Libertador General Bernardo O\'Higgins',
    'Maule', 'Ñuble', 'Biobío', 'La Araucanía', 'Los Ríos', 'Los Lagos',
    'Aysén del General Carlos Ibáñez del Campo', 'Magallanes y de la Antártica Chilena'
  ],
  MX: [
    'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas',
    'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango', 'Estado de México',
    'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Michoacán', 'Morelos', 'Nayarit',
    'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí',
    'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán',
    'Zacatecas'
  ],
  BR: [
    'Acre', 'Alagoas', 'Amapá', 'Amazonas', 'Bahia', 'Ceará', 'Distrito Federal',
    'Espírito Santo', 'Goiás', 'Maranhão', 'Mato Grosso', 'Mato Grosso do Sul',
    'Minas Gerais', 'Pará', 'Paraíba', 'Paraná', 'Pernambuco', 'Piauí',
    'Rio de Janeiro', 'Rio Grande do Norte', 'Rio Grande do Sul', 'Rondônia',
    'Roraima', 'Santa Catarina', 'São Paulo', 'Sergipe', 'Tocantins'
  ],
  AR: [
    'Buenos Aires', 'Catamarca', 'Chaco', 'Chubut', 'Ciudad Autónoma de Buenos Aires',
    'Córdoba', 'Corrientes', 'Entre Ríos', 'Formosa', 'Jujuy', 'La Pampa', 'La Rioja',
    'Mendoza', 'Misiones', 'Neuquén', 'Río Negro', 'Salta', 'San Juan', 'San Luis',
    'Santa Cruz', 'Santa Fe', 'Santiago del Estero', 'Tierra del Fuego', 'Tucumán'
  ],
  EC: [
    'Azuay', 'Bolívar', 'Cañar', 'Carchi', 'Chimborazo', 'Cotopaxi', 'El Oro',
    'Esmeraldas', 'Galápagos', 'Guayas', 'Imbabura', 'Loja', 'Los Ríos', 'Manabí',
    'Morona Santiago', 'Napo', 'Orellana', 'Pastaza', 'Pichincha', 'Santa Elena',
    'Santo Domingo de los Tsáchilas', 'Sucumbíos', 'Tungurahua', 'Zamora Chinchipe'
  ],
  PE: [
    'Amazonas', 'Áncash', 'Apurímac', 'Arequipa', 'Ayacucho', 'Cajamarca', 'Callao',
    'Cusco', 'Huancavelica', 'Huánuco', 'Ica', 'Junín', 'La Libertad', 'Lambayeque',
    'Lima', 'Loreto', 'Madre de Dios', 'Moquegua', 'Pasco', 'Piura', 'Puno',
    'San Martín', 'Tacna', 'Tumbes', 'Ucayali'
  ]
};

const MUNICIPALITIES_BY_REGION = {
  // Venezuela
  'Miranda': ['Acevedo', 'Andrés Bello', 'Baruta', 'Brión', 'Buroz', 'Carrizal', 'Chacao', 'Cristóbal Rojas', 'El Hatillo', 'Guaicaipuro', 'Independencia', 'Lander', 'Los Salias', 'Páez', 'Paz Castillo', 'Pedro Gual', 'Plaza', 'Simón Bolívar', 'Sucre', 'Urdaneta', 'Zamora'],
  'Distrito Capital': ['Libertador'],
  'Zulia': ['Almirante Padilla', 'Baralt', 'Cabimas', 'Catatumbo', 'Colón', 'Francisco Javier Pulgar', 'Jesús Enrique Lossada', 'Jesús María Semprún', 'La Cañada de Urdaneta', 'Lagunillas', 'Machiques de Perijá', 'Mara', 'Maracaibo', 'Miranda', 'Páez', 'Rosario de Perijá', 'San Francisco', 'Santa Rita', 'Simón Bolívar', 'Sucre', 'Valmore Rodríguez'],
  'Amazonas': ['Alto Orinoco', 'Atabapo', 'Atures', 'Autana', 'Manapiare', 'Maroa', 'Río Negro'],
  'Anzoátegui': ['Anaco', 'Aragua', 'Bolívar', 'Bruzual', 'Cajigal', 'Carvajal', 'Diego Bautista Urbaneja', 'Freites', 'Guanipa', 'Guanta', 'Independencia', 'Libertad', 'McGregor', 'Miranda', 'Monagas', 'Peñalver', 'Píritu', 'San Juan de Capistrano', 'Santa Ana', 'Simón Rodríguez', 'Sotillo'],
  'Apure': ['Achaguas', 'Biruaca', 'Muñoz', 'Páez', 'Pedro Camejo', 'Rómulo Gallegos', 'San Fernando'],
  'Aragua': ['Bolívar', 'Camatagua', 'Francisco Linares Alcántara', 'Girardot', 'José Ángel Lamas', 'José Félix Ribas', 'José Rafael Revenga', 'Libertador', 'Mario Briceño Iragorry', 'Ocumare de la Costa de Oro', 'San Casimiro', 'San Sebastián', 'Santiago Mariño', 'Santos Michelena', 'Sucre', 'Tovar', 'Urdaneta', 'Zamora'],
  'Barinas': ['Alberto Arvelo Torrealba', 'Andrés Eloy Blanco', 'Antonio José de Sucre', 'Arismendi', 'Barinas', 'Bolívar', 'Cruz Paredes', 'Ezequiel Zamora', 'Obispos', 'Pedraza', 'Rojas', 'Sosa'],
  'Bolívar': ['Angostura', 'Caroní', 'Cedeño', 'El Callao', 'Gran Sabana', 'Heres', 'Piar', 'Roscio', 'Sifontes', 'Sucre'],
  'Carabobo': ['Bejuma', 'Carlos Arvelo', 'Diego Ibarra', 'Guacara', 'Juan José Mora', 'Libertador', 'Los Guayos', 'Miranda', 'Montalbán', 'Naguanagua', 'Puerto Cabello', 'San Diego', 'San Joaquín', 'Valencia'],
  'Cojedes': ['Anzoátegui', 'Falcón', 'Girardot', 'Lima Blanco', 'Pao de San Juan Bautista', 'Ricaurte', 'Rómulo Gallegos', 'San Carlos', 'Tinaco'],
  'Delta Amacuro': ['Antonio Díaz', 'Casacoima', 'Pedernales', 'Tucupita'],
  'Falcón': ['Acosta', 'Bolívar', 'Buchivacoa', 'Cacique Manaure', 'Carirubana', 'Colina', 'Dabajuro', 'Democracia', 'Falcón', 'Federación', 'Jacura', 'Los Taques', 'Mauroa', 'Miranda', 'Monseñor Iturriza', 'Palmasola', 'Petit', 'Píritu', 'San Francisco', 'Silva', 'Sucre', 'Tocópero', 'Unión', 'Urumaco', 'Zamora'],
  'Guárico': ['Camaguán', 'Chaguaramas', 'El Socorro', 'Francisco de Miranda', 'José Félix Ribas', 'José Tadeo Monagas', 'Juan Germán Roscio', 'Juan José Rondón', 'Julián Mellado', 'Las Mercedes', 'Leonardo Infante', 'Pedro Zaraza', 'Ortiz', 'San Gerónimo de Guayabal', 'San José de Guaribe'],
  'Lara': ['Andrés Eloy Blanco', 'Crespo', 'Iribarren', 'Jiménez', 'Morán', 'Palavecino', 'Simón Planas', 'Torres', 'Urdaneta'],
  'Mérida': ['Alberto Adriani', 'Andrés Bello', 'Antonio Pinto Salinas', 'Aricagua', 'Arzobispo Chacón', 'Campo Elías', 'Caracciolo Parra Olmedo', 'Cardenal Quintero', 'Guaraque', 'Julio César Salas', 'Justo Briceño', 'Libertador', 'Miranda', 'Obispo Ramos de Lora', 'Padre Noguera', 'Pueblo Llano', 'Rangel', 'Rivas Dávila', 'Santos Marquina', 'Sucre', 'Tovar', 'Tulio Febres Cordero', 'Zea'],
  'Monagas': ['Acosta', 'Aguasay', 'Bolívar', 'Caripe', 'Cedeño', 'Ezequiel Zamora', 'Libertador', 'Maturín', 'Piar', 'Punceres', 'Santa Bárbara', 'Sotillo', 'Uracoa'],
  'Nueva Esparta': ['Antolín del Campo', 'Arismendi', 'Díaz', 'García', 'Gómez', 'Maneiro', 'Marcano', 'Mariño', 'Península de Macanao', 'Tubores', 'Villalba'],
  'Portuguesa': ['Agua Blanca', 'Araure', 'Esteller', 'Guanare', 'Guanarito', 'Monseñor José Vicente de Unda', 'Ospino', 'Páez', 'Papelón', 'San Genaro de Boconoíto', 'San Rafael de Onoto', 'Santa Rosalía', 'Sucre', 'Turén'],
  'Sucre': ['Andrés Eloy Blanco', 'Andrés Mata', 'Arismendi', 'Benítez', 'Bermúdez', 'Bolívar', 'Cajigal', 'Cruz Salmerón Acosta', 'Libertador', 'Mariño', 'Mejía', 'Montes', 'Ribero', 'Sucre', 'Valdez'],
  'Táchira': ['Andrés Bello', 'Antonio Rómulo Costa', 'Ayacucho', 'Bolívar', 'Cárdenas', 'Córdoba', 'Fernández Feo', 'Francisco de Miranda', 'García de Hevia', 'Guásimos', 'Independencia', 'Jáuregui', 'José María Vargas', 'Junín', 'Libertad', 'Libertador', 'Lobatera', 'Michelena', 'Panamericano', 'Pedro María Ureña', 'Rafael Urdaneta', 'Samuel Darío Maldonado', 'San Cristóbal', 'Seboruco', 'Simón Rodríguez', 'Sucre', 'Torbes', 'Uribante', 'San Judas Tadeo'],
  'Trujillo': ['Andrés Bello', 'Boconó', 'Bolívar', 'Candelaria', 'Carache', 'Escuque', 'José Felipe Márquez Cañizales', 'Juan Vicente Campo Elías', 'La Ceiba', 'Miranda', 'Monte Carmelo', 'Motatán', 'Pampán', 'Pampanito', 'Rafael Rangel', 'San Rafael de Carvajal', 'Sucre', 'Trujillo', 'Urdaneta', 'Valera'],
  'Vargas': ['Vargas'],
  'Yaracuy': ['Arístides Bastidas', 'Bolívar', 'Bruzual', 'Cocorote', 'Independencia', 'José Antonio Páez', 'La Trinidad', 'Manuel Monge', 'Nirgua', 'Peña', 'San Felipe', 'Sucre', 'Urachiche', 'Veroes'],
  
  // Mantener los municipios existentes de otros países...
  // Colombia
  'Antioquia': ['Medellín', 'Bello', 'Envigado', 'Itagüí', 'Sabaneta', 'La Estrella'],
  'Cundinamarca': ['Bogotá', 'Soacha', 'Chía', 'Mosquera', 'Funza', 'Madrid'],
  'Valle del Cauca': ['Cali', 'Palmira', 'Yumbo', 'Jamundí', 'Buenaventura'],

  // Chile - Región Metropolitana de Santiago (52 comunas)
  'Metropolitana de Santiago': [
    // Provincia de Santiago (32 comunas)
    'Cerrillos', 'Cerro Navia', 'Conchalí', 'El Bosque', 'Estación Central', 'Huechuraba', 
    'Independencia', 'La Cisterna', 'La Florida', 'La Granja', 'La Pintana', 'La Reina', 
    'Las Condes', 'Lo Barnechea', 'Lo Espejo', 'Lo Prado', 'Macul', 'Maipú', 'Ñuñoa', 
    'Pedro Aguirre Cerda', 'Peñalolén', 'Providencia', 'Pudahuel', 'Quilicura', 
    'Quinta Normal', 'Recoleta', 'Renca', 'San Joaquín', 'San Miguel', 'San Ramón', 
    'Santiago', 'Vitacura',
    // Provincia de Chacabuco (3 comunas)
    'Colina', 'Lampa', 'Tiltil',
    // Provincia de Cordillera (3 comunas)
    'Pirque', 'Puente Alto', 'San José de Maipo',
    // Provincia de Maipo (4 comunas)
    'Buin', 'Calera de Tango', 'Paine', 'San Bernardo',
    // Provincia de Melipilla (5 comunas)
    'Alhué', 'Curacaví', 'María Pinto', 'Melipilla', 'San Pedro',
    // Provincia de Talagante (5 comunas)
    'El Monte', 'Isla de Maipo', 'Padre Hurtado', 'Peñaflor', 'Talagante'
  ],
  // Chile - Región de Valparaíso
  'Valparaíso': [
    'Valparaíso', 'Viña del Mar', 'Concón', 'Quilpué', 'Villa Alemana', 'Casablanca',
    'El Quisco', 'El Tabo', 'Cartagena', 'San Antonio', 'Algarrobo', 'Santo Domingo',
    'Quintero', 'Puchuncaví', 'Zapallar', 'Papudo', 'La Ligua', 'Petorca', 'Cabildo',
    'Los Andes', 'San Felipe', 'Putaendo', 'Santa María', 'Panquehue', 'Catemu',
    'Llaillay', 'San Esteban', 'Rinconada', 'Calle Larga', 'Limache', 'Olmué',
    'Quillota', 'La Calera', 'Hijuelas', 'Nogales', 'La Cruz', 'Isla de Pascua', 'Juan Fernández'
  ],
  // Chile - Región del Biobío
  'Biobío': [
    'Concepción', 'Talcahuano', 'San Pedro de la Paz', 'Chiguayante', 'Hualpén',
    'Coronel', 'Lota', 'Penco', 'Tomé', 'Florida', 'Hualqui', 'Santa Juana',
    'Los Ángeles', 'Nacimiento', 'Negrete', 'Mulchén', 'Quilaco', 'Santa Bárbara',
    'Alto Biobío', 'Antuco', 'Cabrero', 'Laja', 'Quilleco', 'San Rosendo',
    'Tucapel', 'Yumbel', 'Arauco', 'Cañete', 'Contulmo', 'Curanilahue', 'Lebu',
    'Los Álamos', 'Tirúa'
  ],
  // Chile - Otras regiones
  'Arica y Parinacota': ['Arica', 'Camarones', 'General Lagos', 'Putre'],
  'Tarapacá': ['Iquique', 'Alto Hospicio', 'Pozo Almonte', 'Camiña', 'Colchane', 'Huara', 'Pica'],
  'Antofagasta': ['Antofagasta', 'Mejillones', 'Sierra Gorda', 'Taltal', 'Calama', 'Ollagüe', 'San Pedro de Atacama', 'Tocopilla', 'María Elena'],
  'Atacama': ['Copiapó', 'Caldera', 'Tierra Amarilla', 'Chañaral', 'Diego de Almagro', 'Vallenar', 'Alto del Carmen', 'Freirina', 'Huasco'],
  'Coquimbo': ['La Serena', 'Coquimbo', 'Andacollo', 'La Higuera', 'Paihuano', 'Vicuña', 'Illapel', 'Canela', 'Los Vilos', 'Salamanca', 'Ovalle', 'Combarbalá', 'Monte Patria', 'Punitaqui', 'Río Hurtado'],
  'Libertador General Bernardo O\'Higgins': ['Rancagua', 'Codegua', 'Coinco', 'Coltauco', 'Doñihue', 'Graneros', 'Las Cabras', 'Machalí', 'Malloa', 'Mostazal', 'Olivar', 'Peumo', 'Pichidegua', 'Quinta de Tilcoco', 'Rengo', 'Requínoa', 'San Vicente', 'San Fernando', 'Chépica', 'Chimbarongo', 'Lolol', 'Nancagua', 'Palmilla', 'Peralillo', 'Placilla', 'Pumanque', 'Santa Cruz', 'Pichilemu', 'La Estrella', 'Litueche', 'Marchihue', 'Navidad', 'Paredones'],
  'Maule': ['Talca', 'Constitución', 'Curepto', 'Empedrado', 'Maule', 'Pelarco', 'Pencahue', 'Río Claro', 'San Clemente', 'San Rafael', 'Cauquenes', 'Chanco', 'Pelluhue', 'Curicó', 'Hualañé', 'Licantén', 'Molina', 'Rauco', 'Romeral', 'Sagrada Familia', 'Teno', 'Vichuquén', 'Linares', 'Colbún', 'Longaví', 'Parral', 'Retiro', 'San Javier', 'Villa Alegre', 'Yerbas Buenas'],
  'Ñuble': ['Chillán', 'Bulnes', 'Cobquecura', 'Coelemu', 'Coihueco', 'Chillán Viejo', 'El Carmen', 'Ninhue', 'Ñiquén', 'Pemuco', 'Pinto', 'Portezuelo', 'Quillón', 'Quirihue', 'Ránquil', 'San Carlos', 'San Fabián', 'San Ignacio', 'San Nicolás', 'Treguaco', 'Yungay'],
  'La Araucanía': ['Temuco', 'Carahue', 'Cunco', 'Curarrehue', 'Freire', 'Galvarino', 'Gorbea', 'Lautaro', 'Loncoche', 'Melipeuco', 'Nueva Imperial', 'Padre Las Casas', 'Perquenco', 'Pitrufquén', 'Pucón', 'Saavedra', 'Teodoro Schmidt', 'Toltén', 'Vilcún', 'Villarrica', 'Cholchol', 'Angol', 'Collipulli', 'Curacautín', 'Ercilla', 'Lonquimay', 'Los Sauces', 'Lumaco', 'Purén', 'Renaico', 'Traiguén', 'Victoria'],
  'Los Ríos': ['Valdivia', 'Corral', 'Lanco', 'Los Lagos', 'Máfil', 'Mariquina', 'Paillaco', 'Panguipulli', 'La Unión', 'Futrono', 'Lago Ranco', 'Río Bueno'],
  'Los Lagos': ['Puerto Montt', 'Calbuco', 'Cochamó', 'Fresia', 'Frutillar', 'Los Muermos', 'Llanquihue', 'Maullín', 'Puerto Varas', 'Castro', 'Ancud', 'Chonchi', 'Curaco de Vélez', 'Dalcahue', 'Puqueldón', 'Queilén', 'Quellón', 'Quemchi', 'Quinchao', 'Osorno', 'Puerto Octay', 'Purranque', 'Puyehue', 'Río Negro', 'San Juan de la Costa', 'San Pablo', 'Chaitén', 'Futaleufú', 'Hualaihué', 'Palena'],
  'Aysén del General Carlos Ibáñez del Campo': ['Coyhaique', 'Lago Verde', 'Aysén', 'Cisnes', 'Guaitecas', 'Cochrane', 'O\'Higgins', 'Tortel', 'Chile Chico', 'Río Ibáñez'],
  'Magallanes y de la Antártica Chilena': ['Punta Arenas', 'Laguna Blanca', 'Río Verde', 'San Gregorio', 'Cabo de Hornos', 'Antártica', 'Porvenir', 'Primavera', 'Timaukel', 'Natales', 'Torres del Paine']
};

// Agregar después de las constantes existentes
const OPERATION_TYPES = [
  { value: "venta", label: "Venta" },
  { value: "arriendo", label: "Arriendo" },
  { value: "temporal", label: "Arriendo Temporal" }
];

// Datos de ejemplo para generar contenido
const SAMPLE_TITLES = [
  "Lujoso apartamento con vista panorámica",
  "Casa moderna en zona exclusiva",
  "Amplio apartamento remodelado",
  "Hermosa casa con jardín",
  "Apartamento de lujo en zona premium",
  "Residencia familiar con piscina",
  "Penthouse con terraza panorámica",
  "Casa contemporánea con acabados de lujo"
];

const SAMPLE_DESCRIPTIONS = [
  "Espectacular propiedad que combina elegancia y confort. Amplios espacios, excelente iluminación natural y acabados de primera calidad. Ubicada en una zona privilegiada con fácil acceso a todos los servicios.",
  "Moderna propiedad diseñada para ofrecer el máximo confort. Espacios bien distribuidos, ventanales que permiten abundante luz natural y materiales de primera calidad. Perfecta para familias que buscan comodidad y exclusividad.",
  "Impresionante propiedad que destaca por su diseño contemporáneo y funcional. Ambientes amplios y luminosos, perfectos para disfrutar en familia. Ubicación estratégica cerca de centros comerciales, colegios y áreas verdes."
];

const SAMPLE_FEATURES = [
  "Internet de alta velocidad",
  "Seguridad 24/7",
  "TV Cable",
  "Gimnasio",
  "Sala de estar",
  "Cocina equipada",
  "Área de juegos infantiles"
];

const SAMPLE_SERVICES = [
  "Agua",
  "Electricidad",
  "Gas",
  "Internet",
  "Cable TV",
  "Mantenimiento",
  "Limpieza"
];

const SAMPLE_COMMON_AREAS = [
  "Piscina",
  "Área social",
  "Parque infantil",
  "Gimnasio",
  "Sala de reuniones",
  "BBQ"
];

const SAMPLE_AREA_DESCRIPTIONS = [
  "Zona residencial tranquila y segura, con excelente acceso a vías principales. Cerca de centros comerciales, supermercados y colegios de prestigio.",
  "Ubicación privilegiada en sector exclusivo, rodeado de áreas verdes y con todos los servicios necesarios a pocos minutos.",
  "Sector en constante desarrollo con alta plusvalía. Excelente conectividad y acceso a todos los servicios básicos y comercios."
];

export default function PropertyManagement() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("details");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [progress, setProgress] = useState(0);
  const [imagePreviews, setImagePreviews] = useState<{ file: File; preview: string; }[]>([]);
  const [isAddressVerified, setIsAddressVerified] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('VE');
  const [selectedState, setSelectedState] = useState('');
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<NominatimSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<PropertyFormData>({
    title: '',
    description: '',
    price: 0,
    operationType: '',
    propertyType: '',
    address: '',
    city: '',
    location: {
      lat: DEFAULT_CENTER.lat,
      lng: DEFAULT_CENTER.lng
    },
    beds: 0,
    baths: 0,
    sqft: 0,
    estacionamientos: 0,
    antiguedad: 0,
    condition: '',
    orientacion: '',
    piso: undefined,
    amoblado: false,
    features: [],
    images: [],
    zonasComunes: [],
    servicios: [],
    descripcionZona: '',
    vendedor: {
      nombre: user?.name || '',
      descripcion: 'Agente inmobiliario profesional',
      telefono: '',
      email: user?.email || ''
    }
  });

  // Cargar datos de la propiedad si estamos en modo edición
  useEffect(() => {
    const fetchPropertyData = async () => {
      if (!id) return;

      try {
        console.log(`[PropertyManagement] Cargando propiedad para editar: ${id}`);
        const response = await axiosInstance.get(`/api/properties/${id}`);
        const property = response.data.property;

        if (!property) {
          throw new Error("No se encontró la propiedad");
        }

        console.log("[PropertyManagement] Datos de propiedad cargados:", property);

        // Mapear datos del backend al formulario
        setFormData({
          title: property.title || '',
          description: property.description || '',
          price: property.price || 0,
          operationType: property.type === 'sale' ? 'Venta' : 'Alquiler',
          propertyType: property.propertyType || '',
          address: property.location?.address || '',
          city: property.location?.city || '',
          location: {
            lat: property.location?.coordinates?.lat || DEFAULT_CENTER.lat,
            lng: property.location?.coordinates?.lng || DEFAULT_CENTER.lng
          },
          beds: property.bedrooms || 0,
          baths: property.bathrooms || 0,
          sqft: property.area || 0,
          estacionamientos: property.parking || 0,
          antiguedad: property.yearBuilt ? (new Date().getFullYear() - property.yearBuilt) : 0,
          condition: property.condition || '',
          orientacion: property.orientation || '',
          piso: property.floor?.toString(),
          amoblado: property.furnished || false,
          features: property.features || [],
          images: property.images || [],
          zonasComunes: property.commonAreas || [],
          servicios: property.services || [],
          descripcionZona: property.areaDescription || '',
          vendedor: property.seller || {
            nombre: user?.name || '',
            descripcion: 'Agente inmobiliario profesional',
            telefono: '',
            email: user?.email || ''
          }
        });

        // Configurar estados de ubicación
        if (property.location?.country) {
          const countryEntry = COUNTRIES.find(c => c.name === property.location.country);
          if (countryEntry) setSelectedCountry(countryEntry.code);
        }
        
        setSelectedState(property.location?.state || '');
        
        // El municipio suele ser la primera parte de city si se guardó como "Municipio, Estado"
        if (property.location?.city && property.location.city.includes(',')) {
          setSelectedMunicipality(property.location.city.split(',')[0].trim());
        } else {
          setSelectedMunicipality(property.location?.city || '');
        }

        // Si tiene coordenadas válidas, marcar como verificado
        if (property.location?.coordinates?.lat && property.location?.coordinates?.lng) {
          setIsAddressVerified(true);
        }

        // Cargar previsualizaciones de imágenes existentes
        if (property.images && property.images.length > 0) {
          const previews = property.images.map((url: string) => {
            // Si es una ruta relativa de la API, completarla si es necesario
            const fullUrl = url.startsWith('http') ? url : `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}${url}`;
            return { file: null as any, preview: fullUrl };
          });
          // Nota: setImagePreviews espera objetos con File, pero para imágenes existentes el File será null
          setImagePreviews(previews);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("[PropertyManagement] Error al cargar propiedad:", error);
        toast({
          title: "Error",
          description: "No se pudo cargar la información de la propiedad",
          variant: "destructive"
        });
        navigate("/dashboard/properties");
      }
    };

    if (isEditMode) {
      fetchPropertyData();
    }
  }, [id, isEditMode, navigate, toast, user]);

  const venezuelaStates = [
    'Amazonas', 'Anzoátegui', 'Apure', 'Aragua', 'Barinas', 'Bolívar', 'Carabobo',
    'Cojedes', 'Delta Amacuro', 'Distrito Capital', 'Falcón', 'Guárico', 'Lara',
    'Mérida', 'Miranda', 'Monagas', 'Nueva Esparta', 'Portuguesa', 'Sucre', 'Táchira',
    'Trujillo', 'Vargas', 'Yaracuy', 'Zulia'
  ];

  const getMunicipalities = (state: string) => {
    const municipalitiesMap: { [key: string]: string[] } = {
      'Amazonas': ['Alto Orinoco', 'Atabapo', 'Atures', 'Autana', 'Manapiare', 'Maroa', 'Río Negro'],
      'Anzoátegui': ['Anaco', 'Aragua', 'Bolívar', 'Bruzual', 'Cajigal', 'Carvajal', 'Freites', 'Guanipa', 'Guanta', 'Independencia', 'Libertad', 'McGregor', 'Miranda', 'Monagas', 'Peñalver', 'Píritu', 'San Juan de Capistrano', 'Santa Ana', 'Simón Rodríguez', 'Sotillo', 'Urbaneja'],
      'Apure': ['Achaguas', 'Biruaca', 'Muñoz', 'Páez', 'Pedro Camejo', 'Rómulo Gallegos', 'San Fernando'],
      'Aragua': ['Bolívar', 'Camatagua', 'Girardot', 'José Ángel Lamas', 'José Félix Ribas', 'José Rafael Revenga', 'Libertador', 'Mario Briceño Iragorry', 'Ocumare de la Costa de Oro', 'San Casimiro', 'San Sebastián', 'Santiago Mariño', 'Santos Michelena', 'Sucre', 'Tovar', 'Urdaneta', 'Zamora'],
      'Barinas': ['Alberto Arvelo Torrealba', 'Andrés Eloy Blanco', 'Antonio José de Sucre', 'Arismendi', 'Barinas', 'Bolívar', 'Cruz Paredes', 'Ezequiel Zamora', 'Obispos', 'Pedraza', 'Rojas', 'Sosa'],
      'Bolívar': ['Angostura', 'Caroní', 'Cedeño', 'El Callao', 'Gran Sabana', 'Heres', 'Piar', 'Roscio', 'Sifontes', 'Sucre'],
      'Carabobo': ['Bejuma', 'Carlos Arvelo', 'Diego Ibarra', 'Guacara', 'Juan José Mora', 'Libertador', 'Los Guayos', 'Miranda', 'Montalbán', 'Naguanagua', 'Puerto Cabello', 'San Diego', 'San Joaquín', 'Valencia'],
      'Cojedes': ['Anzoátegui', 'Falcón', 'Girardot', 'Lima Blanco', 'Pao de San Juan Bautista', 'Ricaurte', 'Rómulo Gallegos', 'San Carlos', 'Tinaco'],
      'Delta Amacuro': ['Antonio Díaz', 'Casacoima', 'Pedernales', 'Tucupita'],
      'Distrito Capital': ['Libertador'],
      'Falcón': ['Acosta', 'Bolívar', 'Buchivacoa', 'Cacique Manaure', 'Carirubana', 'Colina', 'Dabajuro', 'Democracia', 'Falcón', 'Federación', 'Jacura', 'Los Taques', 'Mauroa', 'Miranda', 'Monseñor Iturriza', 'Palmasola', 'Petit', 'Píritu', 'San Francisco', 'Silva', 'Sucre', 'Tocópero', 'Unión', 'Urumaco', 'Zamora'],
      'Guárico': ['Camaguán', 'Chaguaramas', 'El Socorro', 'Francisco de Miranda', 'José Félix Ribas', 'José Tadeo Monagas', 'Juan Germán Roscio', 'Juan José Rondón', 'Julián Mellado', 'Las Mercedes', 'Leonardo Infante', 'Pedro Zaraza', 'Ortiz', 'San Gerónimo de Guayabal', 'San José de Guaribe'],
      'Lara': ['Andrés Eloy Blanco', 'Crespo', 'Iribarren', 'Jiménez', 'Morán', 'Palavecino', 'Simón Planas', 'Torres', 'Urdaneta'],
      'Mérida': ['Alberto Adriani', 'Andrés Bello', 'Antonio Pinto Salinas', 'Aricagua', 'Arzobispo Chacón', 'Campo Elías', 'Caracciolo Parra Olmedo', 'Cardenal Quintero', 'Guaraque', 'Julio César Salas', 'Justo Briceño', 'Libertador', 'Miranda', 'Obispo Ramos de Lora', 'Padre Noguera', 'Pueblo Llano', 'Rangel', 'Rivas Dávila', 'Santos Marquina', 'Sucre', 'Tovar', 'Tulio Febres Cordero', 'Zea'],
      'Miranda': ['Acevedo', 'Andrés Bello', 'Baruta', 'Brión', 'Buroz', 'Carrizal', 'Chacao', 'Cristóbal Rojas', 'El Hatillo', 'Guaicaipuro', 'Independencia', 'Lander', 'Los Salias', 'Páez', 'Paz Castillo', 'Pedro Gual', 'Plaza', 'Simón Bolívar', 'Sucre', 'Urdaneta', 'Zamora'],
      'Monagas': ['Acosta', 'Aguasay', 'Bolívar', 'Caripe', 'Cedeño', 'Ezequiel Zamora', 'Libertador', 'Maturín', 'Piar', 'Punceres', 'Santa Bárbara', 'Sotillo', 'Uracoa'],
      'Nueva Esparta': ['Antolín del Campo', 'Arismendi', 'Díaz', 'García', 'Gómez', 'Maneiro', 'Marcano', 'Mariño', 'Península de Macanao', 'Tubores', 'Villalba'],
      'Portuguesa': ['Agua Blanca', 'Araure', 'Esteller', 'Guanare', 'Guanarito', 'Monseñor José Vicente de Unda', 'Ospino', 'Páez', 'Papelón', 'San Genaro de Boconoíto', 'San Rafael de Onoto', 'Santa Rosalía', 'Sucre', 'Turén'],
      'Sucre': ['Andrés Eloy Blanco', 'Andrés Mata', 'Arismendi', 'Benítez', 'Bermúdez', 'Bolívar', 'Cajigal', 'Cruz Salmerón Acosta', 'Libertador', 'Mariño', 'Mejía', 'Montes', 'Ribero', 'Sucre', 'Valdez'],
      'Táchira': ['Andrés Bello', 'Antonio Rómulo Costa', 'Ayacucho', 'Bolívar', 'Cárdenas', 'Córdoba', 'Fernández Feo', 'Francisco de Miranda', 'García de Hevia', 'Guásimos', 'Independencia', 'Jáuregui', 'José María Vargas', 'Junín', 'Libertad', 'Libertador', 'Lobatera', 'Michelena', 'Panamericano', 'Pedro María Ureña', 'Rafael Urdaneta', 'Samuel Darío Maldonado', 'San Cristóbal', 'Seboruco', 'Simón Rodríguez', 'Sucre', 'Torbes', 'Uribante', 'San Judas Tadeo'],
      'Trujillo': ['Andrés Bello', 'Boconó', 'Bolívar', 'Candelaria', 'Carache', 'Escuque', 'José Felipe Márquez Cañizales', 'Juan Vicente Campo Elías', 'La Ceiba', 'Miranda', 'Monte Carmelo', 'Motatán', 'Pampán', 'Pampanito', 'Rafael Rangel', 'San Rafael de Carvajal', 'Sucre', 'Trujillo', 'Urdaneta', 'Valera'],
      'Vargas': ['Vargas'],
      'Yaracuy': ['Arístides Bastidas', 'Bolívar', 'Bruzual', 'Cocorote', 'Independencia', 'José Antonio Páez', 'La Trinidad', 'Manuel Monge', 'Nirgua', 'Peña', 'San Felipe', 'Sucre', 'Urachiche', 'Veroes']
    };
    return municipalitiesMap[state] || [];
  };

  // Coordenadas aproximadas de los estados/regiones
  const stateCoordinates: { [key: string]: { center: { lat: number, lng: number }, radius: number } } = {
    // Venezuela
    'Amazonas': { center: { lat: 3.8855, lng: -65.1237 }, radius: 2 },
    'Anzoátegui': { center: { lat: 8.2896, lng: -64.6897 }, radius: 1 },
    'Apure': { center: { lat: 7.8878, lng: -67.4724 }, radius: 1.5 },
    'Aragua': { center: { lat: 10.2353, lng: -67.5941 }, radius: 0.5 },
    'Barinas': { center: { lat: 8.6232, lng: -70.2075 }, radius: 1 },
    'Bolívar': { center: { lat: 6.7538, lng: -63.5769 }, radius: 2 },
    'Carabobo': { center: { lat: 10.1620, lng: -68.0076 }, radius: 0.5 },
    'Cojedes': { center: { lat: 9.6612, lng: -68.5833 }, radius: 0.8 },
    'Delta Amacuro': { center: { lat: 8.8742, lng: -61.1465 }, radius: 1 },
    'Distrito Capital': { center: { lat: 10.4806, lng: -66.9036 }, radius: 0.2 },
    'Falcón': { center: { lat: 11.4056, lng: -69.6772 }, radius: 1 },
    'Guárico': { center: { lat: 8.7559, lng: -66.2247 }, radius: 1.5 },
    'Lara': { center: { lat: 10.0647, lng: -69.3570 }, radius: 1 },
    'Mérida': { center: { lat: 8.5897, lng: -71.1561 }, radius: 0.8 },
    'Miranda': { center: { lat: 10.4722, lng: -66.8358 }, radius: 0.5 },
    'Monagas': { center: { lat: 9.7457, lng: -63.1832 }, radius: 1 },
    'Nueva Esparta': { center: { lat: 10.9577, lng: -63.8635 }, radius: 0.3 },
    'Portuguesa': { center: { lat: 9.0859, lng: -69.7524 }, radius: 1 },
    'Sucre': { center: { lat: 10.4567, lng: -63.2870 }, radius: 1 },
    'Táchira': { center: { lat: 7.7669, lng: -72.2249 }, radius: 0.8 },
    'Trujillo': { center: { lat: 9.3658, lng: -70.4369 }, radius: 0.8 },
    'Vargas': { center: { lat: 10.6020, lng: -66.9307 }, radius: 0.3 },
    'Yaracuy': { center: { lat: 10.3399, lng: -68.7425 }, radius: 0.8 },
    'Zulia': { center: { lat: 10.6666, lng: -71.6121 }, radius: 1.5 },
    
    // Chile - Regiones
    'Arica y Parinacota': { center: { lat: -18.4783, lng: -70.3126 }, radius: 1.5 },
    'Tarapacá': { center: { lat: -20.2140, lng: -70.1522 }, radius: 2 },
    'Antofagasta': { center: { lat: -23.6509, lng: -70.3975 }, radius: 3 },
    'Atacama': { center: { lat: -27.3668, lng: -70.3323 }, radius: 2.5 },
    'Coquimbo': { center: { lat: -29.9533, lng: -71.3395 }, radius: 2 },
    'Valparaíso': { center: { lat: -33.0472, lng: -71.6127 }, radius: 1.5 },
    'Metropolitana de Santiago': { center: { lat: -33.4489, lng: -70.6693 }, radius: 0.8 },
    'Libertador General Bernardo O\'Higgins': { center: { lat: -34.1654, lng: -70.7406 }, radius: 1.5 },
    'Maule': { center: { lat: -35.4264, lng: -71.6554 }, radius: 1.8 },
    'Ñuble': { center: { lat: -36.6096, lng: -72.1034 }, radius: 1.2 },
    'Biobío': { center: { lat: -36.8261, lng: -73.0498 }, radius: 1.5 },
    'La Araucanía': { center: { lat: -38.9489, lng: -72.3311 }, radius: 1.8 },
    'Los Ríos': { center: { lat: -39.8142, lng: -73.2459 }, radius: 1.2 },
    'Los Lagos': { center: { lat: -41.4717, lng: -72.9369 }, radius: 2 },
    'Aysén del General Carlos Ibáñez del Campo': { center: { lat: -45.5752, lng: -72.0662 }, radius: 3 },
    'Magallanes y de la Antártica Chilena': { center: { lat: -53.1638, lng: -70.9171 }, radius: 4 },
    
    // Colombia - Regiones principales
    'Antioquia': { center: { lat: 6.2442, lng: -75.5812 }, radius: 1.5 },
    'Cundinamarca': { center: { lat: 4.7110, lng: -74.0721 }, radius: 1.2 },
    'Valle del Cauca': { center: { lat: 3.4516, lng: -76.5320 }, radius: 1.3 }
  };

  // No longer using Google Maps initialization effect
  useEffect(() => {
    // This effect is now empty as we switched to Leaflet
  }, []);

  // Update the form when state or municipality changes
  useEffect(() => {
    if (selectedState && selectedMunicipality) {
      setFormData(prev => ({
        ...prev,
        city: `${selectedMunicipality}, ${selectedState}`
      }));
    }
  }, [selectedState, selectedMunicipality]);

  // Removed Google Maps logic
  useEffect(() => {
    // Empty effect
  }, []);

  // Removed Google Maps logic
  useEffect(() => {
    // Empty effect
  }, []);

  // Validación de campos obligatorios
  const validateForm = (): boolean => {
    const requiredFields = {
      title: 'Título',
      description: 'Descripción',
      price: 'Precio',
      operationType: 'Tipo de operación',
      address: 'Dirección',
      city: 'Ciudad',
      state: 'Estado',
      beds: 'Habitaciones',
      baths: 'Baños',
      sqft: 'Área'
    };

    const emptyFields = Object.entries(requiredFields).filter(
      ([key]) => {
        const value = formData[key as keyof PropertyFormData];
        if (key === 'state') return !selectedState;
        return !value || (typeof value === 'number' && value <= 0);
      }
    );

    if (emptyFields.length > 0) {
      toast({
        title: "Campos requeridos",
        description: `Por favor completa los siguientes campos: ${emptyFields.map(([_, label]) => label).join(', ')}`,
        variant: "destructive"
      });
      return false;
    }

    if (!isAddressVerified) {
      toast({
        title: "Dirección no verificada",
        description: "Por favor, selecciona una dirección válida del menú desplegable.",
        variant: "destructive"
      });
      return false;
    }

    if (imagePreviews.length === 0) {
      toast({
        title: "Imágenes requeridas",
        description: "Por favor, sube al menos una imagen de la propiedad.",
        variant: "destructive"
      });
      return false;
    }

    if (!formData.location?.lat || !formData.location?.lng) {
      toast({
        title: "Ubicación requerida",
        description: "Por favor, selecciona la ubicación de la propiedad en el mapa.",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!token || !user) {
      toast({
        title: "Error de autenticación",
        description: "Debes iniciar sesión para publicar una propiedad",
        variant: "destructive",
      });
      navigate('/login');
      return;
    }

    setIsSubmitting(true);

    try {
      // Subir imágenes nuevas primero
      const imageUrls: string[] = [];
      
      // Mantener las imágenes existentes que ya son URLs
      const existingImages = formData.images.filter(img => img.startsWith('http') || img.startsWith('/api/images/'));
      imageUrls.push(...existingImages);

      // Mostrar mensaje de carga si hay nuevas imágenes para subir
      const newImages = imagePreviews.filter(img => img.file !== null);
      
      if (newImages.length > 0) {
        toast({
          title: "Subiendo imágenes",
          description: "Por favor espera mientras se suben las nuevas imágenes...",
        });
        
        // Procesar cada nueva imagen
        for (const { file } of newImages) {
          if (!file) continue;
          
          const uploadFormData = new FormData();
          uploadFormData.append('images', file);

          try {
            console.log(`Subiendo imagen: ${file.name}`);
            
            const response = await axiosInstance.post('/api/images/upload', uploadFormData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              timeout: 30000,
            });

            console.log(`Respuesta de subida:`, response.data);
            
            if (response.data && response.data.urls) {
              imageUrls.push(...response.data.urls);
            } else if (response.data && response.data.ids) {
              const urls = response.data.ids.map((id: string) => `/api/images/${id}`);
              imageUrls.push(...urls);
            }
          } catch (uploadError: any) {
            console.error(`Error al subir imagen ${file.name}:`, uploadError);
            toast({
              title: "Error al subir imagen",
              description: uploadError.message || "No se pudo subir una imagen. Continuando...",
              variant: "destructive",
            });
          }
        }
      }

      // Map form data to match server property model
      const countryName = COUNTRIES.find(c => c.code === selectedCountry)?.name || 'Venezuela';
      
      const propertyData = {
        title: formData.title,
        description: formData.description,
        price: formData.price,
        type: formData.operationType.toLowerCase() === 'venta' ? 'sale' : 'rent',
        propertyType: formData.propertyType,
        location: {
          address: formData.address,
          city: formData.city,
          state: selectedState,
          country: countryName,
          coordinates: {
            lat: formData.location.lat,
            lng: formData.location.lng
          }
        },
        images: imageUrls,
        features: formData.features,
        bedrooms: formData.beds,
        bathrooms: formData.baths,
        area: formData.sqft,
        status: isEditMode ? undefined : 'available', // No cambiar estado si es edición a menos que sea explícito
        userId: user.id,
        parking: formData.estacionamientos,
        yearBuilt: new Date().getFullYear() - formData.antiguedad,
        condition: formData.condition,
        orientation: formData.orientacion,
        floor: formData.piso,
        furnished: formData.amoblado,
        commonAreas: formData.zonasComunes,
        services: formData.servicios,
        areaDescription: formData.descripcionZona,
        seller: formData.vendedor
      };

      // Mostrar mensaje de carga
      toast({
        title: isEditMode ? "Actualizando propiedad" : "Publicando propiedad",
        description: "Por favor espera...",
      });

      if (isEditMode) {
        await axiosInstance.put(`/api/properties/${id}`, propertyData);
        toast({
          title: "¡Éxito!",
          description: "Propiedad actualizada correctamente",
        });
      } else {
        await axiosInstance.post('/api/properties', propertyData);
        toast({
          title: "¡Éxito!",
          description: "Propiedad publicada correctamente",
        });
      }

      // Redirigir al dashboard
      navigate('/dashboard/properties');
    } catch (error: any) {
      console.error('Error:', error);
      
      // Mostrar mensaje de error detallado
      toast({
        title: "Error al publicar propiedad",
        description: error.message || "Ocurrió un error al publicar la propiedad. Intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateProgress = () => {
    const requiredFields = [
      formData.title,
      formData.description,
      formData.price,
      formData.address,
      formData.beds,
      formData.baths,
      formData.sqft,
      formData.estacionamientos,
      formData.antiguedad,
      formData.condition,
      formData.orientacion
    ];
    
    const filledFields = requiredFields.filter(field => field !== '').length;
    const hasImages = formData.images.length > 0;
    const hasFeatures = formData.features.length > 0;
    const hasServices = formData.servicios.length > 0;
    
    const totalProgress = (
      (filledFields / requiredFields.length) * 0.5 + 
      (hasImages ? 0.2 : 0) + 
      (hasFeatures ? 0.15 : 0) +
      (hasServices ? 0.15 : 0)
    );
    
    setProgress(Math.round(totalProgress * 100));
  };

  // Función para obtener las regiones según el país
  const getRegionsByCountry = (countryCode: string) => {
    return REGIONS_BY_COUNTRY[countryCode as keyof typeof REGIONS_BY_COUNTRY] || [];
  };

  // Función para obtener los municipios según la región
  const getMunicipalitiesByRegion = (region: string) => {
    return MUNICIPALITIES_BY_REGION[region as keyof typeof MUNICIPALITIES_BY_REGION] || [];
  };

  // Renderizar la sección de dirección
  const renderAddressSection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        <div className="space-y-2">
          <Label>País</Label>
          <Select
            value={selectedCountry}
            onValueChange={(value) => {
              setSelectedCountry(value);
              setSelectedState('');
              setSelectedMunicipality('');
              setFormData(prev => ({ ...prev, city: '' }));
              setIsAddressVerified(false);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecciona un país" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map(country => (
                <SelectItem key={country.code} value={country.code}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Estado/Región</Label>
            <Select
              value={selectedState}
              onValueChange={(value) => {
                setSelectedState(value);
                setSelectedMunicipality('');
                setFormData(prev => ({ ...prev, city: '' }));
                setIsAddressVerified(false);
              }}
              disabled={!selectedCountry}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un estado" />
              </SelectTrigger>
              <SelectContent>
                {getRegionsByCountry(selectedCountry).map(region => (
                  <SelectItem key={region} value={region}>
                    {region}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Municipio/Comuna</Label>
            <Select
              value={selectedMunicipality}
              onValueChange={(value) => {
                setSelectedMunicipality(value);
                setFormData(prev => ({
                  ...prev,
                  city: `${value}, ${selectedState}`
                }));
                setIsAddressVerified(false);
              }}
              disabled={!selectedState}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un municipio" />
              </SelectTrigger>
              <SelectContent>
                {getMunicipalitiesByRegion(selectedState).map(municipality => (
                  <SelectItem key={municipality} value={municipality}>
                    {municipality}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Dirección específica</Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Ingresa la dirección exacta"
              disabled={!selectedMunicipality}
              className="pl-10"
              value={formData.address}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, address: e.target.value }));
                setIsAddressVerified(false);
                if (e.target.value.length > 3) {
                  handleAddressSearch(e.target.value);
                } else {
                  setShowSuggestions(false);
                }
              }}
            />
            {showSuggestions && addressSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
                {addressSuggestions.map((suggestion) => (
                  <button
                    key={suggestion.place_id}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 text-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      handleAddressSelection(suggestion);
                    }}
                  >
                    {suggestion.display_name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {!isAddressVerified && (
            <p className="text-sm text-amber-600 mt-1">
              Por favor, selecciona una dirección válida del menú de sugerencias
            </p>
          )}
        </div>

        {/* Leaflet Interactive Map */}
        <div className="space-y-2">
          <Label>Ubicación en el Mapa</Label>
          <div className="h-[500px] w-full rounded-lg border-2 border-ink overflow-hidden brutalist-border-sm relative z-0">
            <MapContainer 
              center={[formData.location.lat, formData.location.lng]} 
              zoom={15} 
              style={{ height: '100%', width: '100%' }}
              className="z-0"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <InteractiveMap 
                location={formData.location} 
                onLocationChange={(lat, lng) => {
                  setFormData(prev => ({ ...prev, location: { lat, lng } }));
                  // Optional: reverse geocode to get address
                }} 
              />
            </MapContainer>
          </div>
          <p className="text-xs text-gray-500 italic">Haz clic en el mapa o arrastra el marcador para ajustar la ubicación exacta.</p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Descripción de la Zona</Label>
            {isAddressVerified && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const sugerencia = generarDescripcionSugerida();
                  setFormData(prev => ({ ...prev, descripcionZona: sugerencia }));
                }}
              >
                <Info className="h-4 w-4 mr-2" />
                Generar sugerencia
              </Button>
            )}
          </div>
          <Textarea
            value={formData.descripcionZona}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, descripcionZona: e.target.value }));
              updateProgress();
            }}
            placeholder="Describe la zona, puntos de interés cercanos, accesos, etc..."
            rows={6}
            className="resize-none"
          />
          {isAddressVerified && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Sugerencias de puntos a mencionar:</p>
              <ul className="text-sm text-gray-600 space-y-1 list-disc pl-4">
                <li>Accesibilidad y vías principales cercanas</li>
                <li>Centros comerciales y áreas de comercio próximas</li>
                <li>Instituciones educativas en la zona</li>
                <li>Centros de salud y clínicas cercanas</li>
                <li>Áreas verdes, parques o espacios recreativos</li>
                <li>Nivel de seguridad y vigilancia en el sector</li>
                <li>Servicios públicos y su calidad en la zona</li>
                <li>Desarrollo urbanístico y proyectos futuros</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const generarDescripcionSugerida = () => {
    if (!selectedState || !selectedMunicipality || !formData.address) return '';

    const descripcionesPorZona: { [key: string]: string } = {
      'Chacao': `La propiedad se encuentra ubicada en el Municipio Chacao, una de las zonas más exclusivas y seguras de Caracas. El sector cuenta con excelente infraestructura urbana, centros comerciales de primer nivel como el Centro Comercial Sambil y El Tolón, además de estar cerca del Parque del Este. La zona destaca por su alta plusvalía, seguridad privada 24/7 y fácil acceso al transporte público, incluyendo el Metro de Caracas.`,
      
      'Baruta': `Ubicada en el Municipio Baruta, esta propiedad se encuentra en una zona residencial de alto valor. El sector cuenta con prestigiosos colegios y universidades, como la Universidad Simón Bolívar, además de centros comerciales como La Trinidad y Las Mercedes. La zona se caracteriza por su ambiente familiar, áreas verdes y excelente conectividad vial.`,
      
      'El Hatillo': `Situada en El Hatillo, zona que combina la tranquilidad de la montaña con la cercanía a la ciudad. El sector es conocido por su clima agradable, seguridad y ambiente familiar. Cerca encontrará el casco histórico de El Hatillo, restaurantes de primera categoría y el Centro Comercial Paseo El Hatillo. La zona mantiene un equilibrio perfecto entre naturaleza y desarrollo urbano.`,
      
      'Sucre': `Propiedad ubicada en el Municipio Sucre, con fácil acceso a las principales vías de la ciudad. La zona cuenta con todos los servicios básicos, comercios locales y está bien conectada con el transporte público. El sector está en constante desarrollo y mejoramiento urbano.`,
      
      'Libertador': `Ubicada en el Municipio Libertador, corazón histórico y administrativo de Caracas. La zona cuenta con acceso a importantes avenidas, centros culturales, universidades y edificios gubernamentales. El sector combina la riqueza histórica con el desarrollo comercial moderno.`
    };

    // Descripción base según el municipio
    let descripcionBase = descripcionesPorZona[selectedMunicipality] || 
      `La propiedad está ubicada en ${selectedMunicipality}, ${selectedState}, en una zona que combina accesibilidad y comodidad. El sector cuenta con todos los servicios básicos y está bien conectado con las principales vías de la ciudad.`;

    // Agregar información específica según el tipo de propiedad
    if (formData.propertyType === "Apartamento") {
      descripcionBase += ` Ideal para quienes buscan una vida urbana con todas las comodidades a su alcance.`;
    } else if (formData.propertyType === "Casa") {
      descripcionBase += ` Perfecto para familias que buscan tranquilidad y espacio sin alejarse de la ciudad.`;
    }

    return descripcionBase;
  };

  // Función para buscar sugerencias de direcciones usando Nominatim (OpenStreetMap)
  const handleAddressSearch = async (query: string) => {
    if (!query.trim()) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      // Construir la consulta con contexto geográfico
      let searchQuery = query;
      if (selectedMunicipality && selectedState) {
        searchQuery = `${query}, ${selectedMunicipality}, ${selectedState}`;
      } else if (selectedState) {
        searchQuery = `${query}, ${selectedState}`;
      }

      // Configurar parámetros de búsqueda
      const params = new URLSearchParams({
        q: searchQuery,
        format: 'json',
        addressdetails: '1',
        limit: '5',
        countrycodes: selectedCountry.toLowerCase(),
        'accept-language': 'es'
      });

      // Agregar viewbox bias si hay coordenadas disponibles para la región
      if (selectedState && stateCoordinates[selectedState]) {
        const { center, radius } = stateCoordinates[selectedState];
        const viewbox = [
          center.lng - radius, // lon1
          center.lat + radius, // lat1
          center.lng + radius, // lon2
          center.lat - radius  // lat2
        ].join(',');
        params.append('viewbox', viewbox);
        params.append('bounded', '1');
      }

      const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'Propiedades-LATAM-App' // Requerido por la política de uso de Nominatim
        }
      });

      if (!response.ok) {
        throw new Error('Error en la respuesta de Nominatim');
      }

      const predictions: NominatimSuggestion[] = await response.json();

      console.log('Nominatim Predictions:', predictions);
      setAddressSuggestions(predictions);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error al buscar direcciones con Nominatim:', error);
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Función para manejar la selección de una dirección sugerida por Nominatim
  const handleAddressSelection = async (suggestion: NominatimSuggestion) => {
    try {
      const location = {
        lat: parseFloat(suggestion.lat),
        lng: parseFloat(suggestion.lon)
      };

      setFormData(prev => ({
        ...prev,
        address: suggestion.display_name || '',
        location
      }));

      setIsAddressVerified(true);
      setShowSuggestions(false);
    } catch (error) {
      console.error('Error al procesar la selección de dirección:', error);
      toast({
        title: "Error de ubicación",
        description: "No se pudo procesar la dirección seleccionada. Por favor, intenta nuevamente.",
        variant: "destructive"
      });
    }
  };

  // Modificar la sección de características en el componente
  const renderFeaturesSection = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <Label>Características de la Propiedad</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(PROPERTY_FEATURES).map(([feature, { icon: Icon, color }]) => (
            <label
              key={feature}
              className={cn(
                "flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors",
                formData.features.includes(feature)
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 hover:border-gray-300"
              )}
            >
              <input
                type="checkbox"
                className="hidden"
                checked={formData.features.includes(feature)}
                onChange={(e) => {
                  const newFeatures = e.target.checked
                    ? [...formData.features, feature]
                    : formData.features.filter(f => f !== feature);
                  setFormData(prev => ({ ...prev, features: newFeatures }));
                  updateProgress();
                }}
              />
              <Icon className={`h-5 w-5 ${color}`} />
              <span className="text-sm">{feature}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <Label>Servicios Básicos e Instalaciones</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(SERVICES_WITH_ICONS).map(([service, { icon: Icon, color }]) => (
            <label
              key={service}
              className={cn(
                "flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors",
                formData.servicios.includes(service)
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 hover:border-gray-300"
              )}
            >
              <input
                type="checkbox"
                className="hidden"
                checked={formData.servicios.includes(service)}
                onChange={(e) => {
                  const newServices = e.target.checked
                    ? [...formData.servicios, service]
                    : formData.servicios.filter(s => s !== service);
                  setFormData(prev => ({ ...prev, servicios: newServices }));
                  updateProgress();
                }}
              />
              <Icon className={`h-5 w-5 ${color}`} />
              <span className="text-sm">{service}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  // Agregar una sección para el vendedor en el renderizado
  const renderSellerSection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Nombre del vendedor</Label>
          <Input
            value={formData.vendedor.nombre}
            onChange={(e) => {
              setFormData(prev => ({
                ...prev,
                vendedor: {
                  ...prev.vendedor,
                  nombre: e.target.value
                }
              }));
              updateProgress();
            }}
            placeholder="Nombre completo"
            disabled // Deshabilitar ya que usamos el nombre del usuario
          />
        </div>
        <div className="space-y-2">
          <Label>Correo electrónico</Label>
          <Input
            type="email"
            value={formData.vendedor.email}
            onChange={(e) => {
              setFormData(prev => ({
                ...prev,
                vendedor: {
                  ...prev.vendedor,
                  email: e.target.value
                }
              }));
              updateProgress();
            }}
            placeholder="correo@ejemplo.com"
            disabled // Deshabilitar ya que usamos el email del usuario
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Teléfono de contacto</Label>
        <Input
          type="tel"
          value={formData.vendedor.telefono}
          onChange={(e) => {
            setFormData(prev => ({
              ...prev,
              vendedor: {
                ...prev.vendedor,
                telefono: e.target.value
              }
            }));
            updateProgress();
          }}
          placeholder="+58 412 1234567"
        />
      </div>

      <div className="space-y-2">
        <Label>Descripción profesional</Label>
        <Textarea
          value={formData.vendedor.descripcion}
          onChange={(e) => {
            setFormData(prev => ({
              ...prev,
              vendedor: {
                ...prev.vendedor,
                descripcion: e.target.value
              }
            }));
            updateProgress();
          }}
          placeholder="Breve descripción de tu experiencia como agente inmobiliario..."
          rows={4}
        />
      </div>
    </div>
  );

  const generateSampleContent = () => {
    const randomTitle = SAMPLE_TITLES[Math.floor(Math.random() * SAMPLE_TITLES.length)];
    const randomDescription = SAMPLE_DESCRIPTIONS[Math.floor(Math.random() * SAMPLE_DESCRIPTIONS.length)];
    const randomPrice = Math.floor(Math.random() * (500000 - 100000) + 100000);
    const randomBeds = Math.floor(Math.random() * 4) + 2;
    const randomBaths = Math.floor(Math.random() * 3) + 1;
    const randomArea = Math.floor(Math.random() * (200 - 80) + 80);
    const randomParking = Math.floor(Math.random() * 3) + 1;
    const randomAge = Math.floor(Math.random() * 10);
    
    // Seleccionar características aleatorias
    const randomFeatures = SAMPLE_FEATURES
      .sort(() => Math.random() - 0.5)
      .slice(0, Math.floor(Math.random() * 5) + 3);
    
    const randomServices = SAMPLE_SERVICES
      .sort(() => Math.random() - 0.5)
      .slice(0, Math.floor(Math.random() * 4) + 3);
    
    const randomCommonAreas = SAMPLE_COMMON_AREAS
      .sort(() => Math.random() - 0.5)
      .slice(0, Math.floor(Math.random() * 4) + 2);
    
    const randomAreaDescription = SAMPLE_AREA_DESCRIPTIONS[Math.floor(Math.random() * SAMPLE_AREA_DESCRIPTIONS.length)];

    setFormData(prev => ({
      ...prev,
      title: randomTitle,
      description: randomDescription,
      price: randomPrice,
      beds: randomBeds,
      baths: randomBaths,
      sqft: randomArea,
      estacionamientos: randomParking,
      antiguedad: randomAge,
      features: randomFeatures,
      servicios: randomServices,
      zonasComunes: randomCommonAreas,
      descripcionZona: randomAreaDescription,
      condition: CONDITIONS[Math.floor(Math.random() * CONDITIONS.length)],
      orientacion: ORIENTATIONS[Math.floor(Math.random() * ORIENTATIONS.length)]
    }));

    updateProgress();
    toast({
      title: "Contenido generado",
      description: "Se ha generado contenido de ejemplo para la propiedad",
    });
  };

  const conditionOptions = [
    { value: "nuevo", label: "Nuevo" },
    { value: "excelente", label: "Excelente" },
    { value: "bueno", label: "Bueno" },
    { value: "regular", label: "Regular" },
    { value: "por remodelar", label: "Por Remodelar" }
  ];

  // Fix the renderFeatureIcon function with proper type annotation
  const renderFeatureIcon = (component: React.ElementType) => {
    const IconComponent = component;
    return <IconComponent className="h-5 w-5" />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-lg font-medium text-muted-foreground">Cargando información de la propiedad...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <form onSubmit={handleSubmit}>
              <Tabs defaultValue="details" className="w-full" onValueChange={setActiveTab}>
                <div className="sticky top-16 bg-white z-40 border-b shadow-sm">
                  <div className="py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                      <h1 className="text-2xl sm:text-3xl font-bold">{isEditMode ? 'Editar Propiedad' : 'Publicar Propiedad'}</h1>
                      <div className="flex items-center gap-4 w-full sm:w-auto">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={generateSampleContent}
                          className="flex-1 sm:flex-none whitespace-nowrap"
                        >
                          <Info className="h-4 w-4 mr-2" />
                          Generar ejemplo
                        </Button>
                        <Progress value={progress} className="w-[100px] sm:w-[200px]" />
                      </div>
                    </div>
                    <div className="overflow-x-auto -mx-4 px-4">
                      <TabsList className="w-full grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 p-1">
                        <TabsTrigger value="details" className="w-full px-2 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap">
                          Detalles Básicos
                        </TabsTrigger>
                        <TabsTrigger value="features" className="w-full px-2 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap">
                          Características
                        </TabsTrigger>
                        <TabsTrigger value="location" className="w-full px-2 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap">
                          Ubicación
                        </TabsTrigger>
                        <TabsTrigger value="seller" className="w-full px-2 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap">
                          Vendedor
                        </TabsTrigger>
                        <TabsTrigger value="media" className="w-full px-2 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap">
                          Fotos
                        </TabsTrigger>
                      </TabsList>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8">
                  <TabsContent value="details">
                    <Card>
                      <CardContent className="space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                          <div className="col-span-1 sm:col-span-2">
                            <Label>Título del Anuncio</Label>
                            <Input
                              required
                              value={formData.title}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, title: e.target.value }));
                                updateProgress();
                              }}
                              placeholder="Ej: Villa moderna con vista al mar"
                              className="mt-1.5"
                            />
                          </div>

                          <div className="col-span-1 sm:col-span-2">
                            <Label>Descripción</Label>
                            <Textarea
                              required
                              value={formData.description}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, description: e.target.value }));
                                updateProgress();
                              }}
                              placeholder="Describe la propiedad detalladamente..."
                              rows={4}
                              className="mt-1.5"
                            />
                          </div>

                          <div>
                            <Label>Precio (USD)</Label>
                            <Input
                              required
                              type="number"
                              value={formData.price}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, price: Number(e.target.value) }));
                                updateProgress();
                              }}
                              placeholder="Ej: 250000"
                              className="mt-1.5"
                            />
                          </div>

                          <div>
                            <Label>Tipo de Operación</Label>
                            <Select
                              value={formData.operationType}
                              onValueChange={(value) => {
                                setFormData(prev => ({ ...prev, operationType: value }));
                                updateProgress();
                              }}
                            >
                              <SelectTrigger className="mt-1.5">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Venta">Venta</SelectItem>
                                <SelectItem value="Alquiler">Alquiler</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label>Tipo de Propiedad</Label>
                            <Select
                              value={formData.propertyType}
                              onValueChange={(value) => {
                                setFormData(prev => ({ ...prev, propertyType: value }));
                                updateProgress();
                              }}
                            >
                              <SelectTrigger className="mt-1.5">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PROPERTY_TYPES.map(type => (
                                  <SelectItem key={type} value={type}>{type}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label>Condición</Label>
                            <Select
                              value={formData.condition}
                              onValueChange={(value) => setFormData({ ...formData, condition: value })}
                            >
                              <SelectTrigger className="mt-1.5">
                                <SelectValue placeholder="Selecciona la condición" />
                              </SelectTrigger>
                              <SelectContent>
                                {CONDITIONS.map((condition) => (
                                  <SelectItem key={condition} value={condition}>
                                    {condition}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label>Habitaciones</Label>
                            <Input
                              required
                              type="number"
                              value={formData.beds}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, beds: Number(e.target.value) }));
                                updateProgress();
                              }}
                              placeholder="Ej: 3"
                              className="mt-1.5"
                            />
                          </div>

                          <div>
                            <Label>Baños</Label>
                            <Input
                              required
                              type="number"
                              value={formData.baths}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, baths: Number(e.target.value) }));
                                updateProgress();
                              }}
                              placeholder="Ej: 2"
                              className="mt-1.5"
                            />
                          </div>

                          <div>
                            <Label>Área (m²)</Label>
                            <Input
                              required
                              type="number"
                              value={formData.sqft}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, sqft: Number(e.target.value) }));
                                updateProgress();
                              }}
                              placeholder="Ej: 120"
                              className="mt-1.5"
                            />
                          </div>

                          <div>
                            <Label>Estacionamientos</Label>
                            <Input
                              type="number"
                              value={formData.estacionamientos}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, estacionamientos: Number(e.target.value) }));
                                updateProgress();
                              }}
                              placeholder="Ej: 2"
                              className="mt-1.5"
                            />
                          </div>

                          <div>
                            <Label>Antigüedad (años)</Label>
                            <Input
                              required
                              type="number"
                              value={formData.antiguedad}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, antiguedad: Number(e.target.value) }));
                                updateProgress();
                              }}
                              placeholder="Ej: 10"
                              className="mt-1.5"
                            />
                          </div>

                          {formData.propertyType === "Apartamento" && (
                            <>
                              <div>
                                <Label>Piso</Label>
                                <Input
                                  type="number"
                                  value={formData.piso}
                                  onChange={(e) => {
                                    setFormData(prev => ({ 
                                      ...prev, 
                                      piso: e.target.value ? e.target.value.toString() : undefined 
                                    }));
                                    updateProgress();
                                  }}
                                  placeholder="Ej: 5"
                                  className="mt-1.5"
                                />
                              </div>

                              <div>
                                <Label>Orientación</Label>
                                <Select
                                  value={formData.orientacion}
                                  onValueChange={(value) => {
                                    setFormData(prev => ({ ...prev, orientacion: value }));
                                    updateProgress();
                                  }}
                                >
                                  <SelectTrigger className="mt-1.5">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ORIENTATIONS.map(orientation => (
                                      <SelectItem key={orientation} value={orientation}>{orientation}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="features">
                    <Card>
                      <CardContent>
                        {renderFeaturesSection()}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="location">
                    <Card>
                      <CardContent className="space-y-6">
                        {renderAddressSection()}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="seller">
                    <Card>
                      <CardContent className="space-y-6">
                        {renderSellerSection()}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="media">
                    <Card>
                      <CardContent>
                        <div className="space-y-6">
                          <div>
                            <Label>Fotos de la Propiedad</Label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                              {imagePreviews.map((preview, index) => (
                                <div key={index} className="relative group">
                                  <div className="aspect-[4/3] rounded-lg overflow-hidden">
                                    <img
                                      src={preview.preview}
                                      alt={`Preview ${index + 1}`}
                                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setImagePreviews(prev => prev.filter((_, i) => i !== index));
                                      setFormData(prev => ({
                                        ...prev,
                                        images: prev.images.filter((_, i) => i !== index)
                                      }));
                                      updateProgress();
                                    }}
                                    className="absolute top-2 right-2 p-1.5 bg-white/80 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                              {imagePreviews.length < 10 && (
                                <label className="relative aspect-[4/3] flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 hover:border-primary cursor-pointer transition-colors bg-gray-50/50 hover:bg-gray-50">
                                  <Upload className="h-8 w-8 text-gray-400" />
                                  <span className="text-sm text-gray-600">Agregar fotos</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => {
                                      if (e.target.files) {
                                        const remainingSlots = 10 - imagePreviews.length;
                                        const newFiles = Array.from(e.target.files).slice(0, remainingSlots);
                                        const newPreviews = newFiles.map(file => ({
                                          file,
                                          preview: URL.createObjectURL(file)
                                        }));
                                        setImagePreviews(prev => [...prev, ...newPreviews]);
                                        setFormData(prev => ({
                                          ...prev,
                                          images: [...prev.images, ...newFiles.map(file => URL.createObjectURL(file))]
                                        }));
                                        updateProgress();
                                      }
                                    }}
                                  />
                                </label>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </div>
              </Tabs>

              <div className="sticky bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50">
                <div className="container mx-auto px-4 py-4">
                  <div className="max-w-5xl mx-auto flex flex-col sm:flex-row justify-between items-stretch gap-4">
                    <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const tabs = ["details", "features", "location", "seller", "media"];
                          const currentIndex = tabs.indexOf(activeTab);
                          if (currentIndex > 0) {
                            setActiveTab(tabs[currentIndex - 1]);
                          }
                        }}
                        disabled={activeTab === "details"}
                        className="flex-1 sm:flex-none justify-center"
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Anterior
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          const tabs = ["details", "features", "location", "seller", "media"];
                          const currentIndex = tabs.indexOf(activeTab);
                          if (currentIndex < tabs.length - 1) {
                            setActiveTab(tabs[currentIndex + 1]);
                          }
                        }}
                        disabled={activeTab === "media"}
                        className="flex-1 sm:flex-none justify-center"
                      >
                        Siguiente
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                    <Button 
                      type="submit"
                      className="w-full sm:w-auto"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {isEditMode ? 'Actualizando...' : 'Publicando...'}
                        </>
                      ) : (
                        <>
                          {isEditMode ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Actualizar Propiedad
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Publicar Propiedad
                            </>
                          )}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
} 